import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Divider, Paper, Stack } from '@mui/material';
import MyModal from '../MyModal/MyModal';
import JobHistorySection from '../JobStatusUpdateModal/JobHistorySection';
import { jobStatusUpdateModalStyles } from '../JobStatusUpdateModal/JobStatusUpdateModal.styles';
import { formatDateTime } from '../../utils/dateUtils';

// JobStatusUpdateModal'in READ-ONLY versiyonu — CiroTalepleri gibi onay
// ekranlarinda "Is Durumu Goruntule" tetiklendiginde acilir. Guncelleme
// formu / dosya yukleme / tarih editleri YOK — sadece bilgi. Ayni ust
// statusSection + JobHistorySection kullanildigi icin gorsel yapi
// isler > "Is Durumu Guncelle" ile ayni.
const JobStatusViewModal = ({ open, onClose, job }) => {
  const { t } = useTranslation();
  const lastChangedAt = job?.updated_at || job?.created_at || null;

  // Aktif proje bilgileri: job_details ilk match + project_extensions
  // uzerinde en son OTELME/UZATMA (JobStatusUpdateModal ile ayni mantik).
  const projectInfo = React.useMemo(() => {
    if (!job) return null;
    let approvalDate = null;
    let projectStartDate = null;
    let projectEndDate = null;
    if (job.job_details) {
      for (const detail of job.job_details) {
        const d = detail?.detail_data;
        if (d?.approvalDate && !approvalDate) approvalDate = d.approvalDate;
        if (d?.projectStartDate && !projectStartDate) projectStartDate = d.projectStartDate;
        if (d?.projectEndDate && !projectEndDate) projectEndDate = d.projectEndDate;
      }
    }
    const extensions = Array.isArray(job.project_extensions) ? job.project_extensions : [];
    const otelme = extensions.find((e) => e.type === 'OTELME');
    const uzatmalar = extensions.filter((e) => e.type === 'UZATMA');
    if (otelme) {
      if (otelme.startDate) projectStartDate = otelme.startDate;
      if (otelme.endDate) projectEndDate = otelme.endDate;
    }
    if (uzatmalar.length > 0) {
      const lastUzatma = uzatmalar[uzatmalar.length - 1];
      if (lastUzatma.endDate) projectEndDate = lastUzatma.endDate;
    }
    return { approvalDate, projectStartDate, projectEndDate };
  }, [job]);

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('tr-TR') : null);

  // Job type ismi: dinamik `job_type` string user-defined ise i18n key'de
  // yoksa ham metnini goster (aksi halde "jobs.jobTypes.AR_GE Merkezi"
  // gibi ham key gorunuyordu).
  const jobTypeLabel = React.useMemo(() => {
    const jt = job?.job_type;
    if (!jt) return job?.job_type_rel?.name || job?.job_type_obj?.name || '-';
    return t(`jobs.jobTypes.${jt}`, { defaultValue: jt });
  }, [job, t]);

  if (!job) return null;

  const items = [
    job?.project_number != null && { label: t('jobStatusUpdate.fields.projectNumber', { defaultValue: 'Proje No' }), value: job.project_number },
    projectInfo?.approvalDate && { label: t('jobStatusUpdate.fields.approvalDate', { defaultValue: 'Onay Tarihi' }), value: fmt(projectInfo.approvalDate) },
    projectInfo?.projectStartDate && { label: t('jobStatusUpdate.fields.projectStartDate', { defaultValue: 'Proje Başlangıç' }), value: fmt(projectInfo.projectStartDate) },
    projectInfo?.projectEndDate && { label: t('jobStatusUpdate.fields.projectEndDate', { defaultValue: 'Proje Bitiş' }), value: fmt(projectInfo.projectEndDate) },
  ].filter(Boolean);

  return (
    <MyModal
      open={open}
      onClose={onClose}
      title={t('jobs.viewStatus', { defaultValue: 'İş Durumu Görüntüle' })}
      maxWidth="md"
      fullWidth
      disableBackdropClose
      disableEscapeKeyDown
    >
      <Box sx={jobStatusUpdateModalStyles?.container || { p: 1 }}>
        {/* Header: is basligi */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {job.job_title || '-'}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.5, color: 'text.secondary' }}>
            <Typography variant="caption">
              {t('jobs.table.jobType', { defaultValue: 'İş Türü' })}: <strong>{jobTypeLabel}</strong>
            </Typography>
            {lastChangedAt && (
              <Typography variant="caption">
                {t('common.lastChanged', { defaultValue: 'Son Değişiklik' })}: {formatDateTime(lastChangedAt)}
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Mevcut durum + proje detay bilgileri (JobStatusUpdateModal'daki
            statusSection ile ayni gorsel yapi) */}
        <Paper elevation={2} sx={jobStatusUpdateModalStyles?.statusSection || { p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" color="text.primary">
              {t('jobStatusUpdate.currentStatus', { defaultValue: 'Mevcut Durum' })}
            </Typography>
            <Chip
              onClick={() => {}}
              label={job?.job_status || '-'}
              color={
                job.job_status === 'ONAYLANDI' ? 'success'
                : job.job_status === 'DEGERLENDIRMEDE' ? 'info'
                : job.job_status === 'YAZIMDA' ? 'warning'
                : job.job_status === 'TAMAMLANDI' ? 'success'
                : 'default'
              }
              variant="filled"
            />
          </Stack>
          {items.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Stack direction="row" flexWrap="wrap" gap={2.5}>
                {items.map((item) => (
                  <Box key={item.label}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Is aciklamasi (varsa) */}
        {job.description_of_job && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('jobs.form.description', { defaultValue: 'Açıklama' })}
            </Typography>
            <Typography variant="body2">
              {job.description_of_job}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Statu gecmisi + uzatma/oteleme kayitlari — JobStatusUpdateModal
            ile ayni component, read-only zaten (input yok). */}
        <JobHistorySection
          jobDetails={job?.job_details}
          projectExtensions={job?.project_extensions}
          styles={jobStatusUpdateModalStyles}
        />
      </Box>
    </MyModal>
  );
};

export default JobStatusViewModal;
